import { useState, useEffect } from 'react'
import { Search, Lock, Eye, EyeOff, ChevronDown, ChevronUp, Users, Target, MapPin, Award, UserCheck, Megaphone, Home, PoundSterling, GraduationCap, Clock, Settings, Brain, Shield, ClipboardCheck } from 'lucide-react'
import SEOHead from '../../components/SEOHead'
import c9LogoImage from '../../assets/images/C9LogoDarkTheme.png'
import transportUKLogoImage from '../assets/transport-uk-logo-blue.png'
import { sections, DashboardCard } from './TransportUKProposalData'

export default function TransportUKProposal() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedSections, setExpandedSections] = useState({})
  const [searchResults, setSearchResults] = useState(0)
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [isAtTop, setIsAtTop] = useState(true)

  // Secure server-side authentication
  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    
    try {
      const response = await fetch('/.netlify/functions/transport-uk-auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: password,
          action: 'login'
        })
      })

      const result = await response.json()

      if (response.ok && result.success) {
        setIsAuthenticated(true)
        // Token is stored in secure HttpOnly cookie by server
        localStorage.setItem('transport-uk-auth-status', 'authenticated')
      } else {
        alert('Incorrect password. Please contact C9 Recruitment for access.')
      }
    } catch (error) {
      console.error('Authentication error:', error)
      alert('Authentication failed. Please try again or contact C9 Recruitment.')
    }
  }

  // Verify authentication on page load
  useEffect(() => {
    const verifyAuth = async () => {
      const authStatus = localStorage.getItem('transport-uk-auth-status')
      if (authStatus === 'authenticated') {
        try {
          const response = await fetch('/.netlify/functions/transport-uk-auth', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              action: 'verify'
            }),
            credentials: 'include' // Include cookies
          })

          if (response.ok) {
            const result = await response.json()
            if (result.valid) {
              setIsAuthenticated(true)
              // Check if token was refreshed
              if (result.refreshed) {
                console.log('Token auto-refreshed - extended for 30 more days')
              }
            } else {
              localStorage.removeItem('transport-uk-auth-status')
            }
          } else {
            localStorage.removeItem('transport-uk-auth-status')
          }
        } catch (error) {
          console.error('Auth verification error:', error)
          localStorage.removeItem('transport-uk-auth-status')
        }
      }
    }

    verifyAuth()
  }, [])

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }



  // Search functionality
  useEffect(() => {
    if (searchTerm) {
      let count = 0
      sections.forEach(section => {
        const sectionText = JSON.stringify(section.content).toLowerCase()
        if (sectionText.includes(searchTerm.toLowerCase())) {
          count++
        }
      })
      setSearchResults(count)
    } else {
      setSearchResults(0)
    }
  }, [searchTerm])

  // Category mapping for sections
  const getSectionCategory = (sectionId) => {
    if (['position', 'objective'].includes(sectionId)) return 'strategy'
    if (['attraction', 'relocation', 'management', 'assessment', 'mobilisation'].includes(sectionId)) return 'operations'
    if (['proposition', 'training', 'supply'].includes(sectionId)) return 'execution'
    if (['technology', 'compliance'].includes(sectionId)) return 'support'
    if (sectionId === 'expertise') return 'expertise'
    return 'strategy'
  }

  // Smart filtering with both search and category
  const getFilteredSections = () => {
    let filtered = sections

    // Filter by category first
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(section => {
        const category = getSectionCategory(section.id)
        return category === selectedCategory || 
               (selectedCategory === 'strategy' && category === 'expertise') // Include expertise with strategy
      })
    }

    // Then filter by search term
    if (searchTerm) {
      filtered = filtered.filter(section => {
        const searchLower = searchTerm.toLowerCase()
        
        // Search in title
        if (section.title.toLowerCase().includes(searchLower)) return true
        
        // Search in all content fields
        const searchInContent = (obj) => {
          if (typeof obj === 'string') {
            return obj.toLowerCase().includes(searchLower)
          }
          if (Array.isArray(obj)) {
            return obj.some(item => 
              typeof item === 'string' 
                ? item.toLowerCase().includes(searchLower)
                : typeof item === 'object' && searchInContent(item)
            )
          }
          if (typeof obj === 'object' && obj !== null) {
            return Object.values(obj).some(value => searchInContent(value))
          }
          return false
        }
        
        return searchInContent(section.content)
      })
    }

    return filtered
  }

  const filteredSections = getFilteredSections()

  // Update search results count
  useEffect(() => {
    setSearchResults(filteredSections.length)
  }, [searchTerm, selectedCategory, filteredSections.length])

  // Handle scroll to show/hide filter buttons
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY
      setIsAtTop(scrollPosition < 100) // Hide filters when scrolled down more than 100px
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-c9-blue via-c9-blue-alt to-slate-800 flex items-center justify-center p-4">
      <SEOHead 
        title="Transport UK Bus Franchising Proposal - Access Required"
        description="Secure access to C9 Recruitment's Transport UK bus franchising proposal"
          noIndex={true}
        />
        
        {/* Background patterns */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,110,0,0.1) 35px, rgba(255,110,0,0.1) 70px)`
          }}></div>
          <div className="absolute top-20 left-20 w-96 h-96 border-2 border-orange-300 rounded-full opacity-20"></div>
          <div className="absolute bottom-20 right-20 w-64 h-64 border-2 border-orange-300 rounded-full opacity-20"></div>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md relative z-10">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-c9-orange rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-c9-blue mb-2">Transport UK Bus Franchising Proposal</h1>
            <p className="text-gray-600">Contingency Plan for Driver Mobilisation</p>
            <p className="text-sm text-gray-500 mt-2">Prepared by C9 Recruitment for Transport UK</p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            <div className="relative">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Access Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-c9-orange focus:border-transparent transition-all"
                  placeholder="Enter password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-c9-orange hover:bg-c9-orange-alt text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105"
            >
              Access Proposal
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>For access, contact:</p>
            <p className="font-medium text-c9-blue">sam.wharfe@c9-group.co.uk</p>
            <p className="font-medium text-c9-blue">alfie.rae@c9-recruitment.com</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead 
        title="Transport UK Bus Franchising Proposal - C9 Recruitment"
        description="Comprehensive contingency plan for driver mobilisation in Liverpool City Region bus franchising"
        noIndex={true}
      />

      {/* Background patterns */}
      <div className="fixed inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0" style={{
          backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,110,0,0.1) 35px, rgba(255,110,0,0.1) 70px)`
        }}></div>
      </div>

      {/* Header */}
      <header className="bg-gradient-to-r from-c9-blue to-c9-blue-alt text-white shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,0.1) 35px, rgba(255,255,255,0.1) 70px)`
          }}></div>
        </div>
        
        <div className="container-full py-10 relative z-10">
          <div className="text-center">
            {/* Partner Logos */}
            <div className="mb-8 mt-8">
              <div className="flex items-center justify-center gap-6 md:gap-8 mb-6">
                {/* C9 Recruitment Logo */}
                <img 
                  src={c9LogoImage} 
                  alt="C9 Recruitment Logo" 
                  className="h-12 md:h-14 w-auto object-contain drop-shadow-lg brightness-110"
                />
                
                {/* Partnership Indicator */}
                <div className="flex items-center gap-2 text-white/80">
                  <div className="w-6 h-0.5 bg-c9-orange"></div>
                  <span className="text-xs font-medium tracking-wide">FOR</span>
                  <div className="w-6 h-0.5 bg-c9-orange"></div>
                </div>
                
                {/* Transport UK Logo */}
                <img 
                  src={transportUKLogoImage} 
                  alt="Transport UK Logo" 
                  className="h-16 md:h-20 w-auto object-contain drop-shadow-lg"
                  style={{
                    filter: 'brightness(0) invert(1)'
                  }}
                />
              </div>
            </div>

            <h1 className="text-2xl md:text-3xl font-bold text-orange-300 mb-4">
              Contingency Plan for Driver Mobilisation
            </h1>
            <div className="space-y-2 mb-8">
              <div className="text-sm text-white/90">
                                  Prepared by C9 Recruitment for Transport UK
              </div>
              <div className="text-sm text-orange-300">
                Project Start: September 2026
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Search Bar */}
      <div className="bg-white/90 backdrop-blur-sm border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="container-full py-8">
          <div className="relative max-w-2xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search across all sections... Try 'drivers', 'training', 'compliance', 'technology'"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-c9-orange focus:border-transparent transition-all text-base bg-white shadow-sm hover:shadow-md"
            />
            {searchTerm && (
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                {searchResults > 0 ? (
                  <div className="text-sm text-c9-orange font-medium bg-orange-50 px-3 py-1 rounded-full">
                    {searchResults} match{searchResults !== 1 ? 'es' : ''} found
                  </div>
                ) : (
                  <div className="text-sm text-gray-500 bg-gray-50 px-3 py-1 rounded-full">
                    No matches found
                  </div>
                )}
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  title="Clear search"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}
          </div>
          
          {/* Filter Results Summary */}
          {(searchTerm || selectedCategory !== 'all') && (
            <div className="mt-4 text-center">
              <div className="text-sm text-gray-600">
                {searchResults > 0 ? (
                  <>
                    Showing <span className="font-semibold text-c9-orange">{searchResults}</span> section{searchResults !== 1 ? 's' : ''}
                    {searchTerm && (
                      <> matching <span className="font-semibold">"{searchTerm}"</span></>
                    )}
                    {selectedCategory !== 'all' && (
                      <> in <span className="font-semibold capitalize">{selectedCategory}</span> category</>
                    )}
                  </>
                ) : (
                  <>
                    {searchTerm ? (
                      <>
                        No sections found for <span className="font-semibold">"{searchTerm}"</span>
                        {selectedCategory !== 'all' && (
                          <> in <span className="font-semibold capitalize">{selectedCategory}</span> category</>
                        )}. 
                        Try searching for keywords like "drivers", "training", "compliance", or "technology".
                      </>
                    ) : (
                      <>No sections found in <span className="font-semibold capitalize">{selectedCategory}</span> category.</>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-16 z-30 shadow-sm">
        <div className="container-full">
          {/* Dashboard Navigation Categories */}
          <div className={`flex justify-center transition-all duration-300 ${
            isAtTop ? 'py-4 opacity-100 transform translate-y-0' : 'py-0 opacity-0 transform -translate-y-4 pointer-events-none max-h-0 overflow-hidden'
          }`}>
                          <div className="flex space-x-4">
                <button
                  onClick={() => {
                    setSelectedCategory('all')
                    setSearchTerm('')
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'all' 
                      ? 'bg-c9-orange text-white' 
                      : 'text-c9-orange bg-orange-50 hover:bg-orange-100'
                  }`}
                >
                  <Target className="w-4 h-4" />
                  <span>Overview</span>
                </button>

                <button
                  onClick={() => {
                    setSelectedCategory('strategy')
                    setSearchTerm('')
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'strategy' 
                      ? 'bg-blue-600 text-white' 
                      : 'text-blue-700 bg-blue-100 hover:bg-blue-200'
                  }`}
                >
                  <MapPin className="w-4 h-4" />
                  <span>Strategy</span>
                </button>

                <button
                  onClick={() => {
                    setSelectedCategory('operations')
                    setSearchTerm('')
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'operations' 
                      ? 'bg-purple-600 text-white' 
                      : 'text-purple-700 bg-purple-100 hover:bg-purple-200'
                  }`}
                >
                  <Users className="w-4 h-4" />
                  <span>Operations</span>
                </button>

                <button
                  onClick={() => {
                    setSelectedCategory('execution')
                    setSearchTerm('')
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'execution' 
                      ? 'bg-green-600 text-white' 
                      : 'text-green-700 bg-green-100 hover:bg-green-200'
                  }`}
                >
                  <GraduationCap className="w-4 h-4" />
                  <span>Execution</span>
                </button>

                <button
                  onClick={() => {
                    setSelectedCategory('support')
                    setSearchTerm('')
                  }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all duration-300 whitespace-nowrap text-sm font-medium ${
                    selectedCategory === 'support' 
                      ? 'bg-gray-600 text-white' 
                      : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                  }`}
                >
                  <Settings className="w-4 h-4" />
                  <span>Technical</span>
                </button>
              </div>
             </div>
           </div>
       </nav>

      {/* Main Content */}
      <main className="py-3 px-1 md:px-2 lg:px-3">
        <div className="max-w-none mx-auto space-y-1">
          
          {/* Hero Dashboard Overview */}
          <div className="hero-section grid grid-cols-1 md:grid-cols-2 gap-1 mb-2">
            {/* Main Hero Card */}
            <div className="bg-gradient-to-br from-c9-blue via-c9-blue to-c9-blue-alt rounded-2xl p-8 text-white relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="w-8 h-8 text-c9-orange" />
                  <span className="text-sm font-medium text-orange-300 uppercase tracking-wide">Mission Critical</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-black mb-4 leading-tight">
                  Driver Mobilisation for Transport UK Franchising
                </h2>
                <p className="text-lg text-white/90 mb-6 leading-relaxed">
                  Ensuring seamless transition with a fully trained, compliant, and route-ready workforce 
                  ahead of the September 2026 launch.
                </p>
                
                {/* Key Stats Row */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-2xl font-black text-c9-orange">300+</div>
                    <div className="text-sm text-white/80">Drivers Required</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                    <div className="text-2xl font-black text-c9-orange">Wirral or St Helens</div>
                    <div className="text-sm text-white/80">Locations</div>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 md:col-span-1 col-span-2">
                    <div className="text-2xl font-black text-c9-orange">12 weeks</div>
                    <div className="text-sm text-white/80">Minimum Engagement</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Key Metrics Card */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
              <h3 className="text-xl font-bold text-c9-blue mb-6">Project Overview</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Start Date</span>
                  <span className="font-semibold text-c9-blue">Sep 2026</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Locations</span>
                  <span className="font-semibold text-c9-blue">Wirral or St Helens</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">C9 Experience</span>
                  <span className="font-semibold text-c9-orange">35,000+ Driver Database</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Training Partner (Optional)</span>
                  <div className="text-right">
                    <div className="font-semibold text-c9-blue text-sm">Ridgeway Training</div>
                    <div className="font-medium text-c9-blue text-xs">Beverley Bell Consulting & Training</div>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    Ready for Implementation
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dynamic Content Based on Search and Category Filter */}
          {(searchTerm || selectedCategory !== 'all') ? (
            /* Search Results */
            filteredSections.length > 0 ? (
                          <div className="space-y-2">
              <div className="text-center mb-3">
                  <h2 className="text-2xl font-bold text-gray-800 mb-2">
                    {searchTerm ? 'Search Results' : `${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)} Sections`}
                  </h2>
                  <p className="text-gray-600">
                    {searchTerm ? (
                      <>Found {filteredSections.length} section{filteredSections.length !== 1 ? 's' : ''} matching "{searchTerm}"</>
                    ) : (
                      <>Showing {filteredSections.length} section{filteredSections.length !== 1 ? 's' : ''} in {selectedCategory} category</>
                    )}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                  {filteredSections.map((section) => {
                    // Determine theme based on section type
                    let theme = 'strategy'
                    if (['attraction', 'relocation', 'management', 'mobilisation'].includes(section.id)) theme = 'operations'
                    if (['proposition', 'training', 'supply'].includes(section.id)) theme = 'execution'
                    if (['technology', 'compliance'].includes(section.id)) theme = 'support'
                    if (section.id === 'expertise') theme = 'expertise'
                    
                    return (
                      <DashboardCard
                        key={section.id}
                        section={section}
                        theme={theme}
                        size="medium"
                        searchTerm={searchTerm}
                      />
                    )
                  })}
                </div>
              </div>
            ) : (
              /* No Results */
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No Results Found</h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm ? (
                    <>We couldn't find any sections matching "<span className="font-semibold">{searchTerm}</span>"</>
                  ) : (
                    <>No sections available in the <span className="font-semibold">{selectedCategory}</span> category</>
                  )}
                </p>
                <div className="space-y-2 text-sm text-gray-500">
                  <p>Try searching for:</p>
                  <div className="flex flex-wrap justify-center gap-2 mt-3">
                    {['drivers', 'training', 'compliance', 'technology', 'recruitment', 'mobilisation', 'Transport UK'].map((suggestion) => (
                      <button
                        key={suggestion}
                        onClick={() => setSearchTerm(suggestion)}
                        className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700 transition-colors"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )
          ) : (
            /* Default Dashboard Layout */
                          <>
                {/* Strategy Section */}
                <div className="mt-48 mb-8">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-black text-c9-blue mb-3 tracking-wide">STRATEGY</h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-c9-orange to-c9-blue mx-auto rounded-full"></div>
                  </div>
                  <div className="strategy-section grid grid-cols-1 md:grid-cols-2 gap-1">
                                      <DashboardCard section={sections.find(s => s.id === 'objective')} theme="strategy" size="large" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'position')} theme="strategy" size="large" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'expertise')} theme="strategy" size="medium" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'mobilisation')} theme="strategy" size="medium" searchTerm={searchTerm} />
                </div>
                </div>

                {/* Operations Section */}
                <div className="mt-48 mb-8">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-black text-c9-blue mb-3 tracking-wide">OPERATIONS</h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-c9-orange to-c9-blue mx-auto rounded-full"></div>
                  </div>
                  <div className="operations-section grid grid-cols-1 md:grid-cols-2 gap-1">
                    <DashboardCard section={sections.find(s => s.id === 'attraction')} theme="operations" size="large" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'relocation')} theme="operations" size="large" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'management')} theme="operations" size="large" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'assessment')} theme="operations" size="large" searchTerm={searchTerm} />
                  </div>
                </div>

                {/* Execution Section */}
                <div className="mt-48 mb-8">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-black text-c9-blue mb-3 tracking-wide">EXECUTION</h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-c9-orange to-c9-blue mx-auto rounded-full"></div>
                  </div>
                  <div className="execution-section grid grid-cols-1 md:grid-cols-2 gap-1">
                    <DashboardCard section={sections.find(s => s.id === 'proposition')} theme="execution" size="medium" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'training')} theme="execution" size="medium" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'supply')} theme="execution" size="medium" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'committed-partner')} theme="execution" size="medium" searchTerm={searchTerm} />
                  </div>
                </div>

                {/* Technical Section */}
                <div className="mt-48 mb-8">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-black text-c9-blue mb-3 tracking-wide">TECHNICAL</h2>
                    <div className="w-24 h-1 bg-gradient-to-r from-c9-orange to-c9-blue mx-auto rounded-full"></div>
                  </div>
                  <div className="support-section grid grid-cols-1 md:grid-cols-2 gap-1">
                    <DashboardCard section={sections.find(s => s.id === 'technology')} theme="support" size="medium" searchTerm={searchTerm} />
                    <DashboardCard section={sections.find(s => s.id === 'compliance')} theme="support" size="medium" searchTerm={searchTerm} />
                  </div>
                  
                  {/* Centered Compliance Ready Section */}
                  <div className="mt-16 flex justify-center">
                    <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-2xl p-8 border border-green-200 max-w-md w-full">
                      <div className="text-center">
                        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Shield className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="font-bold text-green-800 mb-3 text-lg">Compliance Ready</h3>
                        <p className="text-green-700 mb-2">"Powered by People, Driven by Compliance"</p>
                        <div className="text-sm text-green-600">PAYE & FCSA Umbrella Options Available</div>
                      </div>
                    </div>
                  </div>
                </div>
            </>
          )}

        </div>


      </main>

      {/* Footer */}
      <footer className="bg-c9-blue text-white py-8 mt-16">
        <div className="container-full">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            {/* C9 Branding */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-c9-orange rounded-lg flex items-center justify-center">
                <span className="text-white font-black text-xl">C9</span>
              </div>
              <div>
                <h3 className="text-xl font-bold">C9 Recruitment</h3>
                <p className="text-orange-300">Powered by People, Driven by Compliance</p>
              </div>
            </div>
            
            {/* Transport UK Logo */}
            <div className="flex items-center gap-4">
              <img 
                src={transportUKLogoImage} 
                alt="Transport UK Logo" 
                className="h-10 w-auto object-contain opacity-80"
                style={{ filter: 'brightness(0) invert(1)' }}
              />
            </div>
          </div>
          
          <div className="border-t border-white/20 mt-6 pt-6 text-center">
            <p className="text-gray-300 mb-4">
              This document is confidential and proprietary to C9 Recruitment.
            </p>
            <div className="text-sm text-gray-400">
              <p>For questions or support:</p>
              <p>Sam Wharfe: sam.wharfe@c9-group.co.uk | Alfie Rae: alfie.rae@c9-recruitment.com</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}

